import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Waves, Sun, Music, Clock, Users, CreditCard, Shirt, Cloud, MapPin, Building, Car, Sparkles, Snowflake } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useRef } from 'react';

// Real La Villa images
const mainImage = "/images/facciata-villa.png"; // Facciata principale
const poolImage = "/images/pool-aerial.jpg"; // Piscina vista aerea
const barImage = "/images/pool-night-1.jpeg"; // Pool party night
const nightImage = "/images/pool-night-2.jpeg"; // Night event
const facadeImage2 = "/images/2.png"; // Altra facciata
const aerialImage = "/images/villa-aerial.jpeg"; // Vista aerea

// Winter images
const winterImage1 = "/images/villa-winter-1.jpg";
const winterImage2 = "/images/villa-winter-2.jpg";
const interiorImage = "/images/villa-interior.png";

const stats = [
  { value: "2.800+", label: "mq eventi", icon: Building },
  { value: "22.000+", label: "mq parco", icon: Sun },
  { value: "300", label: "mq piscina", icon: Waves },
  { value: "30+", label: "anni storia", icon: Sparkles },
];

export default function LaVilla() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });
  
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <div className="bg-ivory" data-testid="la-villa-page" ref={containerRef}>
      {/* Hero with Parallax */}
      <section className="relative min-h-[80vh] flex items-end overflow-hidden">
        <motion.div 
          className="absolute inset-0"
          style={{ y }}
        >
          <img
            src={mainImage}
            alt="La Villa Pool"
            className="w-full h-full object-cover object-center md:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ivory via-ivory/35 to-transparent" />
        </motion.div>
        
        <motion.div 
          className="relative z-10 w-full max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24 pb-16 pt-32"
          style={{ opacity }}
        >
          <motion.p
            className="font-body text-xs uppercase tracking-[0.3em] text-champagne mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            Castelfranco Emilia
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            data-testid="page-title"
          >
            <img 
              src="/images/logo-new.png" 
              alt="La Villa" 
              className="h-32 md:h-44 lg:h-56 mb-6"
            />
          </motion.div>
          <motion.p
            className="font-body text-lg md:text-xl text-stone-600 max-w-2xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            Oltre 30 anni di storia nell'intrattenimento e nell'ospitalità. Una venue polifunzionale per eventi, celebrazioni private ed esperienze di alto livello.
          </motion.p>
        </motion.div>
      </section>
      
      {/* Stats Bar */}
      <section className="py-12 bg-night">
        <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <stat.icon className="text-champagne mx-auto mb-3" size={24} strokeWidth={1.5} />
                <p className="font-heading text-3xl md:text-4xl text-ivory mb-1">{stat.value}</p>
                <p className="font-body text-xs uppercase tracking-widest text-stone-400">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Intro Narrative */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1800px] mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="font-accent text-xl md:text-2xl italic text-stone-500 leading-relaxed mb-8">
                "Storicamente nota come 'Golfa', La Villa è stata inaugurata nella sua configurazione attuale il 16 giugno 2023 come venue polifunzionale dedicata a eventi, celebrazioni private e ospitalità evoluta."
              </p>
              <p className="font-body text-stone-600 leading-relaxed mb-6">
                Situata strategicamente tra Modena e Bologna (20 minuti da entrambe), La Villa beneficia di un bacino ampio e diversificato. La struttura è progettata per operare su più format e stagionalità, integrando spazi indoor e outdoor con massima flessibilità organizzativa.
              </p>
              <p className="font-body text-stone-600 leading-relaxed mb-6">
                La venue è autorizzata per eventi complessi: piscina aperta al pubblico, somministrazione di alimenti e bevande, pubblico spettacolo e deroga acustica per eventi all'aperto fino a 2000 persone.
              </p>
              <p className="font-body text-stone-600 leading-relaxed">
                Il nostro obiettivo: creare esperienze coinvolgenti e curate, offrire libertà di espressione e diventare un punto di riferimento contemporaneo per eventi ed esperienze di alto livello. <strong className="text-night">Ogni gesto, una firma.</strong>
              </p>
            </motion.div>
            
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <motion.img
                src={poolImage}
                alt="Relaxing at the pool"
                className="w-full aspect-[4/5] object-cover"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.5 }}
              />
              <motion.div 
                className="absolute -bottom-8 -left-8 bg-champagne text-white p-6 max-w-xs hidden md:block"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                <p className="font-accent text-lg italic">
                  "Un'esperienza che rimane nel cuore."
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Location */}
      <section className="py-16 bg-stone-100">
        <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            <motion.div 
              className="flex items-center gap-3"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <MapPin className="text-champagne" size={24} strokeWidth={1.5} />
              <div>
                <p className="font-body text-sm text-stone-500">Indirizzo</p>
                <p className="font-body font-semibold text-night">Via Emilia Est 85/b, Castelfranco Emilia</p>
              </div>
            </motion.div>
            <motion.div 
              className="flex items-center gap-3"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Car className="text-champagne" size={24} strokeWidth={1.5} />
              <div>
                <p className="font-body text-sm text-stone-500">Da Modena</p>
                <p className="font-body font-semibold text-night">20 minuti</p>
              </div>
            </motion.div>
            <motion.div 
              className="flex items-center gap-3"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Car className="text-champagne" size={24} strokeWidth={1.5} />
              <div>
                <p className="font-body text-sm text-stone-500">Da Bologna</p>
                <p className="font-body font-semibold text-night">20 minuti</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* La Villa in ogni stagione - Premium Visual Section */}
      <section className="relative overflow-hidden">
        {/* Large Hero Image with Overlay */}
        <div className="relative h-[70vh] md:h-[80vh]">
          <motion.img
            src={winterImage1}
            alt="La Villa d'inverno"
            className="w-full h-full object-cover"
            initial={{ scale: 1.1 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.5 }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-night/90 via-night/60 to-transparent" />
          
          {/* Content Overlay */}
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24 w-full">
              <motion.div
                className="max-w-2xl"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-[1px] bg-champagne" />
                  <p className="font-body text-xs uppercase tracking-[0.3em] text-champagne">
                    Dodici mesi l'anno
                  </p>
                </div>
                <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-ivory tracking-tight mb-8">
                  La Villa,<br />in ogni stagione
                </h2>
                <p className="font-body text-lg md:text-xl text-ivory/80 leading-relaxed mb-6">
                  La Villa non vive solo d'estate.
                </p>
                <p className="font-body text-ivory/60 leading-relaxed max-w-xl">
                  Nei mesi autunnali e invernali gli spazi interni si trasformano in ambienti eleganti e riservati, perfetti per eventi privati in un'atmosfera intima e raccolta.
                </p>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Bottom Gallery Strip */}
        <div className="bg-night py-16 md:py-24">
          <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
              {/* Summer Card */}
              <motion.div
                className="relative group cursor-pointer overflow-hidden"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                <img
                  src={poolImage}
                  alt="Estate a La Villa"
                  className="w-full h-72 md:h-80 object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-night via-night/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Sun size={18} className="text-champagne" />
                    <span className="font-body text-xs uppercase tracking-widest text-champagne">Estate</span>
                  </div>
                  <p className="font-heading text-xl text-ivory">Nel giardino e a bordo piscina</p>
                </div>
              </motion.div>

              {/* Winter Card */}
              <motion.div
                className="relative group cursor-pointer overflow-hidden"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <img
                  src={winterImage2}
                  alt="Inverno a La Villa"
                  className="w-full h-72 md:h-80 object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-night via-night/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Snowflake size={18} className="text-blue-300" />
                    <span className="font-body text-xs uppercase tracking-widest text-blue-300">Inverno</span>
                  </div>
                  <p className="font-heading text-xl text-ivory">Nelle sale della villa</p>
                </div>
              </motion.div>

              {/* Interior Card */}
              <motion.div
                className="relative group cursor-pointer overflow-hidden"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                <img
                  src={interiorImage}
                  alt="Interni eleganti"
                  className="w-full h-72 md:h-80 object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-night via-night/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={18} className="text-champagne" />
                    <span className="font-body text-xs uppercase tracking-widest text-champagne">Eventi Privati</span>
                  </div>
                  <p className="font-heading text-xl text-ivory">In ogni stagione</p>
                </div>
              </motion.div>
            </div>

            {/* CTA */}
            <motion.div
              className="text-center mt-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
            >
              <p className="font-accent text-2xl md:text-3xl text-champagne italic mb-6">
                La Villa vive dodici mesi l'anno
              </p>
              <Link to="/eventi-privati">
                <Button className="bg-champagne text-night hover:bg-champagne/90 rounded-full px-10 py-6 text-sm uppercase tracking-widest group">
                  Organizza il tuo evento
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* What You'll Find */}
      <section className="py-24 md:py-32" data-testid="services-section">
        <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <motion.p 
              className="font-body text-xs uppercase tracking-[0.2em] text-champagne mb-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              La Struttura
            </motion.p>
            <motion.h2 
              className="font-heading text-4xl md:text-5xl text-night tracking-tight"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Cosa Trovi
            </motion.h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Waves, title: 'Piscina 300mq', desc: 'Piscina con illuminazione notturna, perfetta per eventi diurni e serali.' },
              { icon: Building, title: '3 Piani Interni', desc: '4 bar, 2 cucine professionali, 8 spogliatoi attrezzati.' },
              { icon: Music, title: 'Audio Professionale', desc: 'Consolle DJ, impianto audio completo e illuminazione scenica.' },
              { icon: Sun, title: 'Parco 22.000mq', desc: 'Ampi spazi verdi per eventi all\'aperto fino a 2000 persone.' },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                className="text-center group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <motion.div 
                  className="inline-flex items-center justify-center w-16 h-16 bg-stone-100 mb-6 group-hover:bg-champagne/10 transition-colors"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  <item.icon className="text-champagne" size={28} strokeWidth={1.5} />
                </motion.div>
                <h3 className="font-heading text-xl text-night mb-3">{item.title}</h3>
                <p className="font-body text-sm text-stone-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Services Grid - LUXURY EXPERIENCE */}
      <section className="py-0 bg-night overflow-hidden">
        <div className="max-w-[2000px] mx-auto">
          {/* Immersive Hero Text */}
          <div className="py-24 md:py-32 px-6 md:px-12 lg:px-24 text-center">
            <motion.p 
              className="font-body text-xs uppercase tracking-[0.4em] text-champagne mb-6"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              Create Your Moment
            </motion.p>
            <motion.h2 
              className="font-heading text-5xl md:text-7xl lg:text-8xl text-white tracking-tight mb-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              L'Evento Perfetto<br />
              <span className="text-champagne">Non Esiste.</span>
            </motion.h2>
            <motion.p 
              className="font-accent text-2xl md:text-3xl italic text-stone-400 max-w-2xl mx-auto"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              Lo creiamo insieme.
            </motion.p>
          </div>
          
          {/* Full Width Image Showcase */}
          <div className="relative">
            <motion.div 
              className="grid grid-cols-4 gap-1"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <div className="col-span-2 aspect-[16/10] overflow-hidden">
                <motion.img 
                  src="/images/evento-torta-blu.jpg" 
                  alt="Birthday celebration" 
                  className="w-full h-full object-cover"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.7 }}
                />
              </div>
              <div className="col-span-1 aspect-[8/10] overflow-hidden">
                <motion.img 
                  src="/images/evento-palloncini-oro.jpg" 
                  alt="Birthday decoration" 
                  className="w-full h-full object-cover"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.7 }}
                />
              </div>
              <div className="col-span-1 aspect-[8/10] overflow-hidden">
                <motion.img 
                  src="/images/evento-piscina.webp" 
                  alt="Pool party" 
                  className="w-full h-full object-cover"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.7 }}
                />
              </div>
            </motion.div>
          </div>
          
          {/* Experience Cards */}
          <div className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
            <div className="grid md:grid-cols-3 gap-px bg-stone-800">
              {[
                { 
                  title: 'Entertainment',
                  items: ['DJ set professionale', 'Live performance', 'Mangiafuoco & acrobati', 'Show personalizzati'],
                  accent: 'Dal tramonto all\'alba'
                },
                { 
                  title: 'Experience',
                  items: ['Open bar premium', 'Cocktail signature', 'Catering d\'autore', 'Champagne tower'],
                  accent: 'Ogni dettaglio curato'
                },
                { 
                  title: 'Memories',
                  items: ['Servizio fotografico', 'Video professional', 'Photo booth luxury', 'Album esclusivo'],
                  accent: 'Momenti immortali'
                },
              ].map((card, i) => (
                <motion.div
                  key={card.title}
                  className="bg-night p-10 md:p-12 group"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 }}
                >
                  <p className="font-body text-xs uppercase tracking-[0.3em] text-champagne mb-3">
                    {String(i + 1).padStart(2, '0')}
                  </p>
                  <h3 className="font-heading text-3xl text-white mb-8 group-hover:text-champagne transition-colors">
                    {card.title}
                  </h3>
                  <ul className="space-y-4 mb-10">
                    {card.items.map((item) => (
                      <li key={item} className="font-body text-stone-400 flex items-center gap-3">
                        <span className="w-1.5 h-1.5 bg-champagne rounded-full" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="font-accent text-sm italic text-stone-500">
                    {card.accent}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
          
          {/* Birthday Feature with Photo */}
          <div className="grid lg:grid-cols-2">
            <motion.div 
              className="flex items-center justify-center p-8 md:p-12 lg:p-16 bg-stone-900"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src="/images/evento-18-palloncini.jpg" 
                alt="Compleanno 18 anni" 
                className="max-w-full max-h-[600px] object-contain"
              />
            </motion.div>
            <motion.div 
              className="flex flex-col justify-center p-12 md:p-16 lg:p-24"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="font-body text-xs uppercase tracking-[0.3em] text-champagne mb-4">
                Celebrazioni
              </p>
              <h3 className="font-heading text-4xl md:text-5xl text-white tracking-tight mb-6">
                Compleanni,<br />Lauree,<br />Addii Memorabili.
              </h3>
              <p className="font-body text-lg text-stone-400 leading-relaxed mb-8">
                Dai 18 anni ai traguardi più importanti. 
                Ogni festa merita un palcoscenico all'altezza. 
                Decorazioni personalizzate, scenografie uniche, 
                e un team dedicato a rendere ogni momento indimenticabile.
              </p>
              <Link to="/eventi-privati">
                <Button
                  className="bg-champagne text-night hover:bg-white rounded-none px-10 py-6 text-xs uppercase tracking-widest inline-flex"
                >
                  Richiedi Preventivo <ArrowRight className="ml-2" size={16} />
                </Button>
              </Link>
            </motion.div>
          </div>
          
          {/* Closing Statement */}
          <div className="py-24 md:py-32 px-6 text-center border-t border-stone-800">
            <motion.p 
              className="font-accent text-3xl md:text-4xl lg:text-5xl italic text-white max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              "Ogni gesto, una firma."
            </motion.p>
          </div>
        </div>
      </section>
      
      {/* Mini FAQ */}
      <section className="py-24 md:py-32" data-testid="mini-faq">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <motion.p 
              className="font-body text-xs uppercase tracking-[0.2em] text-champagne mb-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              Domande Frequenti
            </motion.p>
            <motion.h2 
              className="font-heading text-3xl md:text-4xl text-night tracking-tight"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              FAQ Rapide
            </motion.h2>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="dress-code" className="bg-white px-6 border-none">
              <AccordionTrigger className="font-heading text-lg text-night hover:text-champagne">
                <div className="flex items-center gap-3">
                  <Shirt size={18} strokeWidth={1.5} className="text-champagne" />
                  Qual è il dress code consigliato?
                </div>
              </AccordionTrigger>
              <AccordionContent className="font-body text-stone-600">
                Durante il giorno, abbigliamento da piscina elegante. Per le serate, consigliamo uno stile smart casual. Evitare abbigliamento sportivo o troppo casual.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="capacita" className="bg-white px-6 border-none">
              <AccordionTrigger className="font-heading text-lg text-night hover:text-champagne">
                <div className="flex items-center gap-3">
                  <Users size={18} strokeWidth={1.5} className="text-champagne" />
                  Qual è la capienza massima?
                </div>
              </AccordionTrigger>
              <AccordionContent className="font-body text-stone-600">
                La venue è autorizzata per eventi all'aperto fino a 1500-2000 persone. Per eventi privati più intimi, offriamo pacchetti da 30 a 100+ persone con uso esclusivo della location.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="orari" className="bg-white px-6 border-none">
              <AccordionTrigger className="font-heading text-lg text-night hover:text-champagne">
                <div className="flex items-center gap-3">
                  <Clock size={18} strokeWidth={1.5} className="text-champagne" />
                  Quali sono gli orari per eventi privati?
                </div>
              </AccordionTrigger>
              <AccordionContent className="font-body text-stone-600">
                L'uso esclusivo della location per i pacchetti "Your Villa" è dalle 20:00 alle 02:00. Orari personalizzati disponibili su richiesta.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="meteo" className="bg-white px-6 border-none">
              <AccordionTrigger className="font-heading text-lg text-night hover:text-champagne">
                <div className="flex items-center gap-3">
                  <Cloud size={18} strokeWidth={1.5} className="text-champagne" />
                  Cosa succede in caso di maltempo?
                </div>
              </AccordionTrigger>
              <AccordionContent className="font-body text-stone-600">
                Grazie ai 3 piani interni con 4 bar e 2 cucine, possiamo gestire gli eventi anche in caso di maltempo. Contattaci per discutere le opzioni.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
          
          <div className="text-center mt-10">
            <Link
              to="/faq"
              className="font-body text-sm uppercase tracking-widest text-night hover:text-champagne transition-colors inline-flex items-center gap-2"
            >
              Tutte le FAQ <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      
      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-night text-center relative overflow-hidden">
        <motion.div
          className="absolute inset-0 opacity-10"
          animate={{
            backgroundPosition: ["0% 0%", "100% 100%"],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: "reverse"
          }}
          style={{
            backgroundImage: "radial-gradient(circle at center, #D4AF37 1px, transparent 1px)",
            backgroundSize: "50px 50px"
          }}
        />
        <div className="max-w-2xl mx-auto px-6 relative z-10">
          <motion.p 
            className="font-accent text-3xl md:text-4xl italic text-stone-400 mb-8"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            "Ogni gesto, una firma."
          </motion.p>
          <motion.h2 
            className="font-heading text-3xl md:text-4xl text-ivory tracking-tight mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Pronto per l'esperienza?
          </motion.h2>
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Button
              asChild
              className="bg-champagne text-white hover:bg-gold rounded-full px-8 py-6 text-sm uppercase tracking-widest"
              data-testid="final-cta-abbonamenti"
            >
              <Link to="/abbonamenti">
                Abbonamenti <ArrowRight className="ml-2" size={16} />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-ivory text-ivory hover:bg-ivory hover:text-night rounded-full px-8 py-6 text-sm uppercase tracking-widest"
              data-testid="final-cta-eventi"
            >
              <Link to="/eventi-privati">Eventi Privati</Link>
            </Button>
          </motion.div>
        </div>
      </section>
      
      {/* Bottom padding for mobile CTA */}
      <div className="h-20 lg:hidden" />
    </div>
  );
}
